import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PrintPage from "@components/print/PrintElements/PrintPage";
import { useAppSelector } from "@/store/hooks";
import { fetchSuscription, SuscriptionDataType } from "@/api/SuscriptionsApi";
import { ClientStatusRowData, generateReportTotal, InterestConfigType, loadClientStatusData, normalizeClientStatusRow, ReportTotalDetail } from "@utils/DocumentStatus";
import ReportPage, { ReportDataProps } from "@components/dynamic/ReportPage";
import moment from "moment";
import { ClientStatusFormData } from "@/formTypes/ClientStatusFormDataType";
import { formatMoneyValue } from "@utils/Currency";
import { CURRENCIES_CODES } from "@utils/Constants";
import { PrintPageDataLoadedPubSub } from "@utils/pubsubs/PubSub";

const ClientPrintStatus = () => {
  const params = useParams()
  const { queryProps = "", autoPrint = false } = params;
  const suscription = useAppSelector((state) => { return state.suscription.suscription || {} });
  const [statusProp, setStatusProp] = useState<ClientStatusFormData>();
  const [reportData, setReportData] = useState<ReportDataProps[]>([]);
  const [reportTotals, setReportTotals] = useState<ReportTotalDetail[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    import("@printerVariation/generic.scss");
  }, []);

  useEffect(() => {
    PrintPageDataLoadedPubSub.publish({ dataLoaded: false })
    loadReportData()
  }, []);

  const loadReportData = async () => {
    try {
      setIsLoading(true)
      const formData = JSON.parse(atob(queryProps));
      setStatusProp(formData)
      const statusData = await loadClientStatusData(formData)

      const interestConfig: InterestConfigType = {
        interestOnDue: Number(suscription?.interest_on_due || 0),
        isInterestOnDue: suscription?.is_interest_on_due || false
      }

      const clientStatusEntries: ClientStatusRowData[] = await Promise.all(statusData.map((r) => normalizeClientStatusRow(`${formData?.endDate}`, r, interestConfig)));

      const currenciesOnReport = new Set<string>();
      const reportData = clientStatusEntries.map(d => {
        // El saldo a favor ya trae su propio prefijo en `numero`.
        let documentPrefix = d.kind == "settlement" ? "L" : "";
        if (d.kind != "clientCredit" && d.interestExempt) {
          documentPrefix += "I"
        }

        const date = moment(d.fecha, "YYYY-MM-DD")
        const formatDate = date.format("DD-MM-YYYY")
        const currencyId = Number(d.currency.currency_id || 1);
        currenciesOnReport.add(CURRENCIES_CODES[currencyId]);

        let data: ReportDataProps = {
          values: [
            `${documentPrefix ? `${documentPrefix}-` : ""}${d.numero}`,
            `${d.nombreFacturacion}`,
            `${formatDate}`,
            `${formatMoneyValue(d.total, currencyId)}`,
            `${formatMoneyValue(d.totalNotes, currencyId)}`,
            `${formatMoneyValue(d.totalPagos, currencyId)}`,
            `${formatMoneyValue(d.saldo, currencyId)}`,
            CURRENCIES_CODES[currencyId],
            `${d.exchangeRate}`,
            `${d.diasFacturado} dias`,
            `${d.diasVencida} dias`,
            `${d.recibo}`
          ]
        }
        return data
      })

      const reportTotal = generateReportTotal(reportData, [
        {
          dataColumnIndex: 3,
          title: "Total Facturas"
        },
        {
          dataColumnIndex: 5,
          title: "Abonos"
        },
        {
          dataColumnIndex: 6,
          title: "Pendiente"
        },
      ], currenciesOnReport, 7);

      setReportData(reportData)
      setReportTotals(reportTotal)
    } finally {
      setIsLoading(false)
      PrintPageDataLoadedPubSub.publish({ dataLoaded: true })
    }
  }

  return (
    <div>
      <PrintPage
        autoPrint={autoPrint === "1"}
        onReadyDebounce={8000}
      >
        <ReportPage
          loading={isLoading}
          suscription={suscription}
          reportConfig={{
            title: "Estado de Cuenta",
            date: `${statusProp?.endDate || ""}`,
          }}
          reportTotals={reportTotals}
          reportData={reportData}
          reportTitles={[
            { label: "# Doc", width: "7%" },
            { label: "Nombre Facturacion", width: "28%" },
            { label: "Fecha", width: "8%" },
            { label: "Total", width: "10%" },
            { label: "Total Notas", width: "10%" },
            { label: "Abonos", width: "10%" },
            { label: "Pendiente", width: "10%" },
            { label: "Moneda", width: "5%" },
            { label: "Tipo Cambio", width: "5%" },
            { label: "Dias Facturado", width: "7%" },
            { label: "Dias Vencida", width: "7%" },
            { label: "# Recibo", width: "8%" }
          ]}
        />
      </PrintPage>

    </div>
  );
}

export default ClientPrintStatus;
